import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import { createXlsxBlob } from '@/shared/utils/xlsxExport';

type AuditItem = {
  id: number;
  fechaHora: string;
  usuarioId: number | null;
  actorNombre: string | null;
  concepto: string | null;
  detalle: string | null;
  accion: string | null;
  entidad: string | null;
  entidadId: string | null;
  metodoHttp: string | null;
  ruta: string | null;
  tenantId: string | null;
  datosAnteriores: string | null;
  datosNuevos: string | null;
  ip: string | null;
  actorTipo: string | null;
};

type AuditResult = {
  items: AuditItem[];
  total: number;
  page: number;
  pageSize: number;
};

type AuditMeta = {
  entidades: string[];
  acciones: string[];
};

type Filters = {
  q: string;
  entidad: string;
  accion: string;
  fechaDesde: string;
  fechaHasta: string;
};

const EMPTY_FILTERS: Filters = {
  q: '',
  entidad: '',
  accion: '',
  fechaDesde: '',
  fechaHasta: '',
};

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-AR');
  } catch {
    return iso;
  }
}

function axiosMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (msg) return msg;
  }
  return err instanceof Error ? err.message : fallback;
}

function prettyJson(raw: string | null): string | null {
  if (!raw?.trim()) return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function actorLabel(item: AuditItem): string {
  if (item.actorNombre?.trim()) return item.actorNombre.trim();
  if (item.usuarioId != null) return `#${item.usuarioId}`;
  if (item.actorTipo === 'job') return 'job';
  if (item.actorTipo === 'webhook') return 'webhook';
  return '—';
}

function actionBadgeClass(accion: string | null): string {
  const value = (accion ?? '').toLowerCase();
  if (value.includes('alta') || value.includes('activ') || value === 'aplicacion') {
    return 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200';
  }
  if (value.includes('baja') || value.includes('delete') || value.includes('desactiv') || value.includes('cancel')) {
    return 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200';
  }
  return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
}

function toQuery(filters: Filters, page: number, pageSize: number) {
  return {
    page,
    pageSize,
    q: filters.q.trim() || undefined,
    entidad: filters.entidad || undefined,
    accion: filters.accion || undefined,
    fechaDesde: filters.fechaDesde ? `${filters.fechaDesde}T00:00:00` : undefined,
    fechaHasta: filters.fechaHasta ? `${filters.fechaHasta}T23:59:59` : undefined,
  };
}

function parseInvoiceChanges(raw: string | null): Array<{
  type?: string;
  beforeText?: string;
  afterText?: string;
  reason?: string;
}> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { changes?: unknown };
    if (!Array.isArray(parsed?.changes) || parsed.changes.length === 0) return null;
    return parsed.changes as Array<{
      type?: string;
      beforeText?: string;
      afterText?: string;
      reason?: string;
    }>;
  } catch {
    return null;
  }
}

export const AuditoriasPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister, canOperateBilling } = useAuth();
  const canView = canAdminister || canOperateBilling;
  const { success, error: showError } = useToast();

  const [draft, setDraft] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [data, setData] = useState<AuditResult | null>(null);
  const [meta, setMeta] = useState<AuditMeta>({ entidades: [], acciones: [] });
  const [selected, setSelected] = useState<AuditItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const res = await httpClient.get<AuditMeta>('/auditoria/meta');
      setMeta({
        entidades: res.data.entidades ?? [],
        acciones: res.data.acciones ?? [],
      });
    } catch {
      /* los filtros siguen usables a mano */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await httpClient.get<AuditResult>('/auditoria', {
        params: toQuery(applied, page, pageSize),
      });
      setData(res.data);
      setSelected((prev) => {
        if (!prev) return res.data.items[0] ?? null;
        return res.data.items.find((item) => item.id === prev.id) ?? res.data.items[0] ?? null;
      });
    } catch (err) {
      showError(axiosMessage(err, t('pages.auditoria.loadError')));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [applied, page, showError, t]);

  useEffect(() => {
    if (canView) void loadMeta();
  }, [canView, loadMeta]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const totalPages = useMemo(() => {
    if (!data) return 0;
    return Math.max(1, Math.ceil(data.total / pageSize));
  }, [data]);

  const applyFilters = () => {
    setPage(1);
    setApplied({ ...draft });
  };

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await httpClient.get<{ items: AuditItem[]; total: number; truncated: boolean }>(
        '/auditoria/export',
        { params: toQuery(applied, 1, 5000) }
      );
      const rows = res.data.items ?? [];
      if (rows.length === 0) {
        showError(t('pages.auditoria.exportEmpty'));
        return;
      }
      const header = [
        t('pages.auditoria.columns.time'),
        t('pages.auditoria.columns.user'),
        t('pages.auditoria.columns.actorType'),
        t('pages.auditoria.columns.action'),
        t('pages.auditoria.columns.entity'),
        t('pages.auditoria.columns.entityId'),
        t('pages.auditoria.columns.concept'),
        t('pages.auditoria.columns.detail'),
        t('pages.auditoria.columns.before'),
        t('pages.auditoria.columns.after'),
        'IP',
        t('pages.auditoria.columns.path'),
      ].map((value) => ({ value, style: 'header' as const }));

      const blob = createXlsxBlob([
        {
          name: 'Auditoria',
          columns: [22, 18, 12, 18, 22, 22, 36, 48, 40, 40, 16, 28],
          rows: [
            [{ value: t('pages.auditoria.exportTitle'), style: 'title' }],
            [{ value: t('pages.auditoria.exportGenerated', { at: new Date().toLocaleString('es-AR') }) }],
            [],
            header,
            ...rows.map((item) => [
              formatTs(item.fechaHora),
              actorLabel(item),
              item.actorTipo ?? '',
              item.accion ?? '',
              item.entidad ?? '',
              item.entidadId ?? '',
              item.concepto ?? '',
              item.detalle ?? '',
              prettyJson(item.datosAnteriores) ?? '',
              prettyJson(item.datosNuevos) ?? '',
              item.ip ?? '',
              [item.metodoHttp, item.ruta].filter(Boolean).join(' '),
            ]),
          ],
        },
      ]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `Auditoria-Coofit-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      if (res.data.truncated) {
        success(t('pages.auditoria.exportTruncated', { count: rows.length, total: res.data.total }));
      } else {
        success(t('pages.auditoria.exportDone', { count: rows.length }));
      }
    } catch (err) {
      showError(axiosMessage(err, t('pages.auditoria.exportError')));
    } finally {
      setExporting(false);
    }
  };

  if (!canView) {
    return (
      <div className="panel-page">
        <h1 className="panel-page-title">{t('pages.auditoria.title')}</h1>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('pages.auditoria.forbidden')}</p>
      </div>
    );
  }

  const invoiceChanges = selected ? parseInvoiceChanges(selected.datosNuevos) : null;

  return (
    <div className="panel-page flex h-full min-w-0 flex-col gap-4 overflow-auto">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="panel-page-title">{t('pages.auditoria.title')}</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('pages.auditoria.subtitle')}</p>
          <p className="mt-1 text-xs text-slate-500">{t('pages.auditoria.immutableHint')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            {t('pages.auditoria.refresh')}
          </Button>
          <Button onClick={() => void handleExport()} disabled={exporting || loading} isLoading={exporting}>
            {t('pages.auditoria.exportExcel')}
          </Button>
        </div>
      </div>

      <div className="panel-card flex flex-wrap items-end gap-3">
        <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
          {t('pages.auditoria.search')}
          <input
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
            value={draft.q}
            onChange={(e) => setDraft((prev) => ({ ...prev, q: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyFilters();
            }}
            placeholder={t('pages.auditoria.searchPlaceholder')}
          />
        </label>
        <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
          {t('pages.auditoria.entity')}
          <select
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
            value={draft.entidad}
            onChange={(e) => setDraft((prev) => ({ ...prev, entidad: e.target.value }))}
          >
            <option value="">{t('pages.auditoria.allEntities')}</option>
            {meta.entidades.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-[10rem] flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
          {t('pages.auditoria.action')}
          <select
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
            value={draft.accion}
            onChange={(e) => setDraft((prev) => ({ ...prev, accion: e.target.value }))}
          >
            <option value="">{t('pages.auditoria.allActions')}</option>
            {meta.acciones.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-w-[9rem] flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
          {t('pages.auditoria.from')}
          <input
            type="date"
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
            value={draft.fechaDesde}
            onChange={(e) => setDraft((prev) => ({ ...prev, fechaDesde: e.target.value }))}
          />
        </label>
        <label className="flex min-w-[9rem] flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
          {t('pages.auditoria.to')}
          <input
            type="date"
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
            value={draft.fechaHasta}
            onChange={(e) => setDraft((prev) => ({ ...prev, fechaHasta: e.target.value }))}
          />
        </label>
        <Button onClick={applyFilters} disabled={loading}>
          {t('pages.auditoria.apply')}
        </Button>
        <Button variant="ghost" onClick={clearFilters} disabled={loading}>
          {t('pages.auditoria.clear')}
        </Button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-5">
        <div className="panel-table-wrap min-h-[20rem] xl:col-span-3">
          {loading ? (
            <p className="p-4 text-sm text-gray-500">{t('common.loading')}</p>
          ) : !data || data.items.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">{t('pages.auditoria.empty')}</p>
          ) : (
            <table className="w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2">{t('pages.auditoria.columns.time')}</th>
                  <th className="px-3 py-2">{t('pages.auditoria.columns.user')}</th>
                  <th className="px-3 py-2">{t('pages.auditoria.columns.action')}</th>
                  <th className="px-3 py-2">{t('pages.auditoria.columns.entity')}</th>
                  <th className="px-3 py-2">{t('pages.auditoria.columns.concept')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.items.map((item) => (
                  <tr
                    key={item.id}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 ${
                      selected?.id === item.id ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                    }`}
                    onClick={() => setSelected(item)}
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600 dark:text-gray-300">
                      {formatTs(item.fechaHora)}
                    </td>
                    <td className="px-3 py-2 text-gray-800 dark:text-gray-100">{actorLabel(item)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${actionBadgeClass(item.accion)}`}>
                        {item.accion ?? '—'}
                      </span>
                    </td>
                    <td className="max-w-[10rem] truncate px-3 py-2 font-mono text-xs">
                      {item.entidad ?? '—'}
                      {item.entidadId ? ` #${item.entidadId}` : ''}
                    </td>
                    <td className="max-w-[16rem] truncate px-3 py-2 text-gray-900 dark:text-gray-100">
                      {item.concepto ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {data && data.total > 0 ? (
            <div className="flex items-center justify-between gap-2 border-t border-gray-200 px-3 py-2 text-xs text-gray-500 dark:border-gray-700">
              <span>
                {t('pages.auditoria.pagination', {
                  total: data.total,
                  page: data.page,
                  pages: totalPages,
                })}
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  {t('common.previous')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="panel-card min-h-[20rem] overflow-auto xl:col-span-2">
          <h2 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
            {t('pages.auditoria.detail')}
          </h2>
          {!selected ? (
            <p className="text-sm text-gray-500">{t('pages.auditoria.selectHint')}</p>
          ) : (
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-xs text-gray-500">{t('pages.auditoria.columns.time')}</div>
                <div>{formatTs(selected.fechaHora)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">{t('pages.auditoria.columns.user')}</div>
                <div>
                  {actorLabel(selected)}
                  {selected.actorTipo ? ` · ${selected.actorTipo}` : ''}
                  {selected.usuarioId != null ? ` · #${selected.usuarioId}` : ''}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">{t('pages.auditoria.columns.action')}</div>
                <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${actionBadgeClass(selected.accion)}`}>
                  {selected.accion ?? '—'}
                </span>
              </div>
              <div>
                <div className="text-xs text-gray-500">{t('pages.auditoria.columns.entity')}</div>
                <div className="font-mono text-xs">
                  {selected.entidad ?? '—'}
                  {selected.entidadId ? ` #${selected.entidadId}` : ''}
                </div>
              </div>
              {selected.concepto ? (
                <div>
                  <div className="text-xs text-gray-500">{t('pages.auditoria.columns.concept')}</div>
                  <div>{selected.concepto}</div>
                </div>
              ) : null}
              {selected.detalle ? (
                <div>
                  <div className="text-xs text-gray-500">{t('pages.auditoria.columns.detail')}</div>
                  <p className="whitespace-pre-wrap break-words">{selected.detalle}</p>
                </div>
              ) : null}
              {invoiceChanges ? (
                <div>
                  <div className="text-xs text-gray-500">{t('pages.auditoria.invoiceChanges')}</div>
                  <div className="mt-1 space-y-2">
                    {invoiceChanges.map((change, index) => (
                      <div key={`${change.type}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs">
                        <div className="font-semibold capitalize">{change.type ?? 'cambio'}</div>
                        <div className="mt-1 text-slate-600">
                          {t('pages.auditoria.columns.before')}: {change.beforeText || '—'}
                        </div>
                        <div className="text-slate-600">
                          {t('pages.auditoria.columns.after')}: {change.afterText || '—'}
                        </div>
                        {change.reason ? (
                          <div className="mt-1 text-slate-800">
                            {t('pages.auditoria.reason')}: {change.reason}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
              {prettyJson(selected.datosAnteriores) ? (
                <div>
                  <div className="text-xs text-gray-500">{t('pages.auditoria.columns.before')}</div>
                  <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-2 text-xs dark:bg-gray-900/60">
                    {prettyJson(selected.datosAnteriores)}
                  </pre>
                </div>
              ) : null}
              {prettyJson(selected.datosNuevos) ? (
                <div>
                  <div className="text-xs text-gray-500">{t('pages.auditoria.columns.after')}</div>
                  <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-gray-50 p-2 text-xs dark:bg-gray-900/60">
                    {prettyJson(selected.datosNuevos)}
                  </pre>
                </div>
              ) : null}
              {selected.ruta ? (
                <div>
                  <div className="text-xs text-gray-500">{t('pages.auditoria.columns.path')}</div>
                  <div className="font-mono text-xs">
                    {selected.metodoHttp} {selected.ruta}
                  </div>
                </div>
              ) : null}
              {selected.ip ? (
                <div>
                  <div className="text-xs text-gray-500">IP</div>
                  <div className="font-mono text-xs">{selected.ip}</div>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
